"use client"

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { SafeImage } from '@/components/ui/safe-image'
import { pickProductImage, RAIL_OVERFETCH, takeIllustratedFirst } from '@/lib/image-url'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Zap, Star, Award, TrendingDown } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { formatPrice } from '@/lib/format-utils'
import { applySafeProductFilters } from '@/lib/product-filters'
import { BRAND_COLORS } from '@/lib/constants'

/** Cards this rail renders. Named so the fetch size and the slice cannot drift. */
const SLOTS = 6


interface Product {
  id: string
  slug: string
  title: string
  brand: string
  productType: string
  capacityCuFt?: number
  price: number
  originalPrice?: number
  rating?: number
  reviewsCount?: number
  isEnergyStar?: boolean
  imageUrl?: string
  features?: string[]
  hasPriceDrop?: boolean
  savings?: number
}

interface EnergyCalculatorRecommendationsProps {
  yearlyCost?: number
  kwhUsage?: string
}

const mapProduct = (p: any) => ({
  id: p.id,
  slug: p.seo_slug || p.id,
  title: p.title,
  brand: p.brand,
  productType: p.type,
  capacityCuFt: p.capacity_cuft,
  price: p.lowest_price_cents ? p.lowest_price_cents / 100 : 0,
  originalPrice: p.lowest_price_ever_cents ? p.lowest_price_ever_cents / 100 : undefined,
  rating: p.specs_json?.rating,
  reviewsCount: p.specs_json?.reviews,
  isEnergyStar: p.specs_json?.energy_star,
  imageUrl: pickProductImage(p) ?? undefined, // FRIDGE-349: mirrored first via the shared selector
  hasPriceDrop: p.lowest_price_ever_cents && p.lowest_price_ever_cents > p.lowest_price_cents,
  savings: p.lowest_price_ever_cents && p.lowest_price_ever_cents > p.lowest_price_cents 
    ? (p.lowest_price_ever_cents - p.lowest_price_cents) / 100 
    : 0
})

export function EnergyCalculatorRecommendations({
  yearlyCost,
  kwhUsage
}: EnergyCalculatorRecommendationsProps) {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const potentialSavings = Math.round(yearlyCost * 0.3) // Energy Star saves ~30%
  
  useEffect(() => {
    async function fetchRecommendations() {
      try {
        const supabase = createClient()
        
        // Full-size refrigerator types only (excludes compact, wine coolers, ice makers, etc.)
        const fullSizeTypes = [
          'french-door',
          'side-by-side', 
          'top-freezer',
          'bottom-freezer',
          'counter-depth'
        ]
        
        // Words that indicate non-full-size products
        const excludeTitleWords = ['compact', 'mini', 'undercounter', 'portable']
        
        // Fetch full-size refrigerators with prices. applySafeProductFilters
        // enforces pdp_eligible + banned-title + banned-brand. constrainTypes:false
        // because .in('type',...) is tighter than the global APPROVED_TYPES list.
        let rawQuery = supabase
          .from('products')
          .select(`
            id, seo_slug, title, brand, type, capacity_cuft, images, images_supabase, specs_json,
            lowest_price_cents, lowest_price_ever_cents
          `)
          .eq('status', 'active')
          .in('type', fullSizeTypes)
          .not('lowest_price_cents', 'is', null)
          .gt('lowest_price_cents', 0)
        rawQuery = applySafeProductFilters(rawQuery, { constrainTypes: false })
        const { data: rawProducts, error: dbError } = await rawQuery
          .order('lowest_price_cents', { ascending: false }) // Start with higher-end for variety
          .limit(100)
        
        if (dbError) {
          setError(dbError.message)
        } else if (rawProducts && rawProducts.length > 0) {
          // Filter for Energy Star, exclude non-full-size by title, deduplicate
          const seenTitles = new Set<string>()
          const brandCounts = new Map<string, number>()
          const filtered: any[] = []
          
          for (const p of rawProducts) {
            const titleLower = p.title?.toLowerCase() || ''
            
            // Must be Energy Star certified (check specs_json OR title)
            const isEnergyStar = p.specs_json?.energy_star === true || 
              titleLower.includes('energy star') || 
              titleLower.includes('energystar')
            if (!isEnergyStar) continue
            
            // Exclude products with mini/compact/etc in title
            if (excludeTitleWords.some(word => titleLower.includes(word))) continue
            
            // Skip duplicates by title
            const titleKey = titleLower.trim()
            if (seenTitles.has(titleKey)) continue
            seenTitles.add(titleKey)
            
            // Limit to 2 products per brand for variety
            const brandCount = brandCounts.get(p.brand) || 0
            if (brandCount >= 2) continue
            brandCounts.set(p.brand, brandCount + 1)
            
            filtered.push(p)
            if (filtered.length >= 6) break
          }
          
          // Only show recommendations if we have real full-size fridges
          if (filtered.length > 0) {
            setProducts(takeIllustratedFirst(filtered, SLOTS).map(mapProduct))
          }
          // No fallback - don't show mini fridges or non-Energy Star products
        }
      } catch (err) {
        // Silently handle - recommendations are optional
        setError('Failed to load recommendations')
      } finally {
        setLoading(false)
      }
    }
    
    fetchRecommendations()
  }, [])
  
  if (loading) {
    return (
      <Card className="mt-6">
        <CardContent className="p-8 text-center">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-48 mx-auto mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-32 mx-auto"></div>
          </div>
        </CardContent>
      </Card>
    )
  }
  
  if (error || products.length === 0) {
    return null
  }
  
  const priceDropCount = products.filter(p => p.hasPriceDrop).length
  
  return (
    <Card className="mt-6 border-gray-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#0469f0]">
          <Zap className="h-5 w-5 text-[#ff9951]" />
          Save {`$${potentialSavings}`}/Year with ENERGY STAR
        </CardTitle>
        <CardDescription className="flex items-center gap-4">
          <span>Based on {kwhUsage} kWh annual usage</span>
          {priceDropCount > 0 && (
            <span className="text-[#ff9951] font-semibold flex items-center gap-1">
              <TrendingDown className="h-3 w-3" />
              {priceDropCount} with price drops!
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <Link 
              key={product.id} 
              href={`/products/${product.slug}`}
              className="block hover:shadow-lg transition-all group"
            >
              <Card className={product.hasPriceDrop ? 'border-[#ff9951]' : ''}>
                <CardContent className="p-4">
                  {/* Price Drop Badge */}
                  {product.hasPriceDrop && product.savings && product.savings > 0 && (
                    <span 
                      className="absolute top-2 right-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold z-10"
                      style={{ backgroundColor: BRAND_COLORS.orange, color: BRAND_COLORS.white }}
                    >
                      Save {formatPrice(product.savings)}
                    </span>
                  )}
                  
                  {/* Product Image */}
                  <div className="relative h-48 mb-3 bg-gray-50 rounded-lg overflow-hidden">
                    {product.imageUrl ? (
                      <SafeImage
                        src={product.imageUrl}
                        alt={product.title ? `${product.title} | Fridge.com` : ''}
                        fill
                        className="object-contain p-4 group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        <span className="text-6xl">🧊</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Product Info */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-[#0469f0]">
                      {product.title}
                    </h3>
                    
                    {/* Energy Badge */}
                    {potentialSavings > 0 && (
                      <span 
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                        style={{ backgroundColor: BRAND_COLORS.orange, color: BRAND_COLORS.white }}
                      >
                        ⚡ Save ~{`$${Math.round(potentialSavings / 12)}`}/month
                      </span>
                    )}
                    
                    {/* Price */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-bold text-[#0469f0]">
                        {formatPrice(product.price)}
                      </span>
                      {product.originalPrice && (
                        <span className="text-sm text-gray-500 line-through">
                          {formatPrice(product.originalPrice)}
                        </span>
                      )}
                    </div>
                    
                    {/* Trust Signals */}
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      {product.rating !== undefined && product.rating > 0 && (
                        <span className="flex items-center gap-0.5 text-black">
                          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                          {product.rating.toFixed(1)}{product.reviewsCount && product.reviewsCount > 0 ? ` (${product.reviewsCount})` : ''}
                        </span>
                      )}
                      {product.isEnergyStar && (
                        <span className="flex items-center gap-0.5" style={{ color: BRAND_COLORS.bluePrimary }}>
                          <Award className="h-3 w-3" style={{ color: BRAND_COLORS.bluePrimary }} />
                          Energy Star
                        </span>
                      )}
                    </div>
                    
                    {/* Energy Savings Badge */}
                    <span 
                      className="flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold w-full"
                      style={{ backgroundColor: BRAND_COLORS.orange, color: BRAND_COLORS.white }}
                    >
                      ✓ Cuts energy costs by ~30%
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
        
        {/* View Collection CTA */}
        <div className="mt-6 text-center">
          <Link href="/collections/energy-star-refrigerators">
            <Button 
              size="lg" 
              className="text-white group"
              style={{ backgroundColor: BRAND_COLORS.bluePrimary }}
            >
              View All Energy Efficient Models
              <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

export default EnergyCalculatorRecommendations
